import React from "react";
import { Link } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { CheckCircle, Server } from "lucide-react";
import { CALENDLY_COST_REVIEW_URL } from "@/lib/urls";
import {
  ProductRecommendationsMockup,
  type MockupRecommendation,
} from "./ProductRecommendationsMockup";

const RECOMMENDATIONS: readonly MockupRecommendation[] = [
  {
    name: "api-prod-01",
    subtext: "us-east-1 · 72 days",
    current: "m5.2xlarge",
    recommended: "m5.xlarge",
    cpuAvg: "4.2%",
    cpuPct: "18%",
    memAvg: "11%",
    memPct: "24%",
    savings: "$47/mo",
  },
  {
    name: "worker-batch-02",
    subtext: "us-east-1 · 52 days",
    current: "c5.xlarge",
    recommended: "c5.large",
    cpuAvg: "8.1%",
    cpuPct: "22%",
    memAvg: "12%",
    memPct: "28%",
    savings: "$23/mo",
  },
  {
    name: "cache-redis-03",
    subtext: "us-west-2 · 84 days",
    current: "r5.xlarge",
    recommended: "r5.large",
    cpuAvg: "3.7%",
    cpuPct: "12%",
    memAvg: "18%",
    memPct: "32%",
    savings: "$38/mo",
  },
];

const TRUST_POINTS = [
  "No credit card required",
  "Read-only IAM role",
  "Your team reviews and merges every change",
] as const;

export function Hero(): React.ReactElement {
  return (
    <section className="px-4 py-16 pt-40">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12">
        <div className="flex-1 text-center lg:text-left">
          <h1 className="mb-4 text-5xl font-bold leading-tight tracking-tight md:text-6xl">
            Cut your AWS bill with{" "}
            <span className="text-primary">automated Terraform PRs</span>
          </h1>
          <p className="max-w-2xl mx-auto lg:mx-0 mb-6 text-xl leading-relaxed text-surface-foreground/80">
            Infralyst analyzes your AWS resources, finds waste, and turns it into
            Terraform pull requests your team reviews and merges.
          </p>
          <p className="max-w-2xl mx-auto lg:mx-0 mb-8 text-sm italic text-surface-foreground/60">
            Every dollar you save is a dollar off your cloud provider's revenue.
            Their tools will never seriously cut your bill.
          </p>
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-6">
            <Button asChild size="lg">
              <Link href="/signin">Start free with 3 PRs</Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <a href={CALENDLY_COST_REVIEW_URL} target="_blank" rel="noopener noreferrer">
                Book a demo
              </a>
            </Button>
          </div>
          <ul className="flex flex-col gap-2 items-center lg:items-start">
            {TRUST_POINTS.map((point) => (
              <li key={point} className="flex items-center gap-2 text-sm text-surface-foreground/60">
                <CheckCircle className="size-4 text-green-500 flex-none" />
                {point}
              </li>
            ))}
          </ul>
        </div>
        <div className="min-w-0 w-full lg:flex-1 lg:max-w-2xl overflow-x-auto">
          <ProductRecommendationsMockup
            title="EC2 Recommendations"
            icon={Server}
            recommendations={RECOMMENDATIONS}
            footerText="Analyzed 42 instances across 3 regions"
            compact
          />
        </div>
      </div>
    </section>
  );
}
